import type { Canvas, FabricObject } from 'fabric';

export type AnimationTypeId = 'fade-in' | 'slide' | 'zoom' | 'bounce' | 'rotate' | 'dissolve';

export interface AnimationConfig {
  type: AnimationTypeId;
  duration: number; // seconds
  loopInfinite: boolean;
  repeatCount: number; // ignored if loopInfinite = true
  delay: number; // seconds before starting
}

export const ANIMATION_TYPES: { id: AnimationTypeId; label: string }[] = [
  { id: 'fade-in', label: 'Fade in' },
  { id: 'slide', label: 'Slide' },
  { id: 'zoom', label: 'Zoom' },
  { id: 'bounce', label: 'Bounce' },
  { id: 'rotate', label: 'Rotate' },
  { id: 'dissolve', label: 'Dissolve' },
];

// Animations that make the element appear: when the whole canvas plays (the preview of all animations,
// exports) the element starts hidden in its first frame instead of showing and then vanishing to come in.
// Bounce and dissolve start from the element as it is, so they never hide it.
export const ENTRANCE_ANIMATIONS: ReadonlySet<AnimationTypeId> = new Set(['fade-in', 'slide', 'zoom', 'rotate']);

// Animations that make the element disappear: in the same playback the element stays gone once they end,
// instead of popping back while the other animations go on.
export const EXIT_ANIMATIONS: ReadonlySet<AnimationTypeId> = new Set(['dissolve']);

export const isEntranceAnimation = (type?: AnimationTypeId): boolean => !!type && ENTRANCE_ANIMATIONS.has(type);

export const isExitAnimation = (type?: AnimationTypeId): boolean => !!type && EXIT_ANIMATIONS.has(type);

export interface PlaybackOptions {
  // Show entrance animations in their first frame while waiting for their delay. Off when previewing a
  // single element, so it stays visible to keep editing it.
  hideBeforeEntrance?: boolean;
  // Leave exit animations in their last frame once they end (until the playback is stopped). Off when
  // previewing a single element, for the same reason.
  hideAfterExit?: boolean;
}

export const DEFAULT_ANIMATION_CONFIG: AnimationConfig = {
  type: 'fade-in',
  duration: 0.8,
  loopInfinite: false,
  repeatCount: 1,
  delay: 0,
};

export type AnimatableObject = FabricObject & {
  animationConfig?: AnimationConfig;
  isGridLine?: boolean;
  isSafeZone?: boolean;
};

export interface ObjectState {
  left: number;
  top: number;
  scaleX: number;
  scaleY: number;
  angle: number;
  opacity: number;
}

type AnimationTransform = Partial<ObjectState>;

// Each animation is a pure function progress(0..1) -> deltas relative to the object's final/original state.
const ANIMATION_DEFINITIONS: Record<AnimationTypeId, (progress: number, original: ObjectState) => AnimationTransform> = {
  'fade-in': (progress, original) => ({ opacity: progress * original.opacity }),
  slide: (progress, original) => ({
    opacity: progress * original.opacity,
    left: original.left - 80 * (1 - progress),
  }),
  zoom: (progress, original) => {
    const scale = 0.3 + 0.7 * progress;
    return {
      opacity: progress * original.opacity,
      scaleX: original.scaleX * scale,
      scaleY: original.scaleY * scale,
    };
  },
  bounce: (progress, original) => {
    const y = Math.abs(Math.sin(progress * Math.PI * 3)) * 40 * (1 - progress);
    return { top: original.top - y };
  },
  rotate: (progress, original) => ({
    opacity: progress * original.opacity,
    angle: original.angle - 30 * (1 - progress),
  }),
  // Fabric has no true per-pixel dissolve; approximated with a fade out.
  dissolve: (progress, original) => ({ opacity: (1 - progress) * original.opacity }),
};

// How long exited elements stay gone after the playback's last animation ends, so the ending can be seen.
const EXIT_HOLD_MS = 1000;

const getCycleDurationMs = (config: AnimationConfig) => Math.max(50, config.duration * 1000);

// Total length of an animation: delay plus its cycles. An infinite loop counts one cycle (an exported
// GIF loops forever anyway).
export const getAnimationTimelineMs = (config: AnimationConfig): number =>
  Math.max(0, config.delay * 1000) + getCycleDurationMs(config) * (config.loopInfinite ? 1 : Math.max(1, config.repeatCount));

export const captureObjectState = (object: FabricObject): ObjectState => ({
  left: object.left ?? 0,
  top: object.top ?? 0,
  scaleX: object.scaleX ?? 1,
  scaleY: object.scaleY ?? 1,
  angle: object.angle ?? 0,
  opacity: object.opacity ?? 1,
});

// State of an animated object `elapsedMs` after the animation starts, as the editor plays it: the original
// state once the cycles end (or the exit's last frame), and while waiting for the delay (or the entrance's
// first frame), see PlaybackOptions. Pure, so exports can render any frame.
export const getAnimationStateAt = (
  config: AnimationConfig,
  original: ObjectState,
  elapsedMs: number,
  { hideBeforeEntrance = false, hideAfterExit = false }: PlaybackOptions = {}
): ObjectState => {
  const cycleMs = getCycleDurationMs(config);
  const sinceStart = elapsedMs - Math.max(0, config.delay * 1000);
  if (!ANIMATION_DEFINITIONS[config.type]) return { ...original };
  if (elapsedMs >= getAnimationTimelineMs(config)) {
    return hideAfterExit && !config.loopInfinite && isExitAnimation(config.type)
      ? { ...original, ...ANIMATION_DEFINITIONS[config.type](1, original) }
      : { ...original };
  }
  if (sinceStart < 0) {
    return hideBeforeEntrance && isEntranceAnimation(config.type)
      ? { ...original, ...ANIMATION_DEFINITIONS[config.type](0, original) }
      : { ...original };
  }
  const progress = (sinceStart % cycleMs) / cycleMs;
  return { ...original, ...ANIMATION_DEFINITIONS[config.type](progress, original) };
};

interface RunningAnimation {
  cancelled: boolean;
  rafId?: number;
  timeoutId?: ReturnType<typeof setTimeout>;
  // Reverts whatever the running animation has applied so far (origin/left/top/
  // scale/angle/opacity). Without this, cancelling midway left the object in a corrupted state
  // that the next playback took as "original", so offsets accumulated on every rapid
  // repeated click (the "stacked" animations bug).
  restore?: () => void;
}

// Each playing object stores its handle here so it can be cancelled if a new
// preview starts before it ends, or if the canvas is destroyed (HMR/unmount).
const runningAnimations = new WeakMap<AnimatableObject, RunningAnimation>();

export const stopObjectAnimation = (object: AnimatableObject) => {
  const running = runningAnimations.get(object);
  if (!running) return;
  running.cancelled = true;
  if (running.timeoutId) clearTimeout(running.timeoutId);
  if (running.rafId) cancelAnimationFrame(running.rafId);
  running.restore?.();
  runningAnimations.delete(object);
};

export const playObjectAnimation = (
  canvas: Canvas,
  object: AnimatableObject,
  { hideBeforeEntrance = false, hideAfterExit = false }: PlaybackOptions = {}
) => {
  const config = object.animationConfig;
  if (!config?.type) return;

  stopObjectAnimation(object);
  const handle: RunningAnimation = { cancelled: false };
  runningAnimations.set(object, handle);

  const original = captureObjectState(object);

  const applyDefinition = ANIMATION_DEFINITIONS[config.type];
  const durationMs = getCycleDurationMs(config);

  const safeRender = () => {
    if (!canvas.disposed) canvas.requestRenderAll();
  };

  const restoreOriginal = () => {
    object.set({ ...original });
    object.setCoords();
    safeRender();
  };

  const runCycle = (onDone: () => void) => {
    if (handle.cancelled || canvas.disposed) return;

    // Fabric 7 objects are centre-origin, so scale/angle already pivot around the centre.
    handle.restore = restoreOriginal;

    const startTime = performance.now();

    const step = (currentTime: number) => {
      if (handle.cancelled || canvas.disposed) return;

      const progress = Math.min(1, (currentTime - startTime) / durationMs);
      object.set({ ...original });
      object.set(applyDefinition(progress, original));
      object.setCoords();
      safeRender();

      if (progress < 1) {
        handle.rafId = requestAnimationFrame(step);
        return;
      }

      restoreOriginal();
      onDone();
    };

    handle.rafId = requestAnimationFrame(step);
  };

  // Hidden right away (not on the first animation frame), so the element never flashes before coming in.
  if (hideBeforeEntrance && isEntranceAnimation(config.type)) {
    handle.restore = restoreOriginal;
    object.set(applyDefinition(0, original));
    object.setCoords();
    safeRender();
  }

  let completedCycles = 0;
  const loop = () => {
    if (handle.cancelled || canvas.disposed) return;
    runCycle(() => {
      completedCycles += 1;
      if (handle.cancelled) return;
      if (config.loopInfinite || completedCycles < config.repeatCount) {
        loop();
      } else if (hideAfterExit && isExitAnimation(config.type)) {
        // Stays registered, so stopping the playback (or grabbing the element) brings it back.
        object.set(applyDefinition(1, original));
        object.setCoords();
        safeRender();
      } else {
        runningAnimations.delete(object);
      }
    });
  };

  handle.timeoutId = setTimeout(() => {
    if (!handle.cancelled) loop();
  }, Math.max(0, config.delay * 1000));
};

// End of the current playback of all animations, which brings back the elements that exited.
const playbackEndTimers = new WeakMap<Canvas, ReturnType<typeof setTimeout>>();

export const stopAllAnimations = (canvas: Canvas) => {
  clearTimeout(playbackEndTimers.get(canvas));
  playbackEndTimers.delete(canvas);
  (canvas.getObjects() as AnimatableObject[]).forEach(stopObjectAnimation);
};

export const playAllAnimations = (canvas: Canvas) => {
  stopAllAnimations(canvas);
  const animated = (canvas.getObjects() as AnimatableObject[]).filter(
    (object) => !object.isGridLine && !object.isSafeZone && object.animationConfig?.type
  );
  animated.forEach((object) => playObjectAnimation(canvas, object, { hideBeforeEntrance: true, hideAfterExit: true }));

  // Once the longest animation ends (like the export's timeline) the playback is over: exited elements
  // come back so they can be edited again.
  if (!animated.some((object) => isExitAnimation(object.animationConfig?.type))) return;
  const playbackMs = Math.max(...animated.map((object) => getAnimationTimelineMs(object.animationConfig as AnimationConfig)));
  playbackEndTimers.set(
    canvas,
    setTimeout(() => {
      playbackEndTimers.delete(canvas);
      animated.forEach(stopObjectAnimation);
    }, playbackMs + EXIT_HOLD_MS)
  );
};
